import type { FC, MouseEventHandler, RefObject } from 'react'
import type ReactImageGallery from 'react-image-gallery'

import { useState, useContext } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { ImageGallery, ButtonIcon, Icon } from 'components'
import { ListingIdContext } from 'context/listingIdContext'
import { useTracker } from 'store/@hooks/use-tracker'
import { selectPublicListingPhotos } from 'store/public-listing'
import { LISTING_PUBLIC_PAGE } from 'tracker/events'

interface ListingGalleryProps {
  canFullScreen?: boolean
}

type renderFullscreenButtonProps = {
  onClick: React.MouseEventHandler<HTMLElement>
  isFullscreen: boolean
}

const FullScreenButtonWrap: React.FC<IStyledButton> = styled.button<IStyledButton>`
  display: inline-block;
  height: 31px;
  width: 31px;
`

const FullScreenButton = (props: renderFullscreenButtonProps) => {
  const { onClick, isFullscreen } = props
  return (
    <FullScreenButtonWrap
      id="image-gallery-fullscreen-button-wrap-icon"
      className="image-gallery-icon image-gallery-fullscreen-button"
      onClick={onClick}
    >
      {isFullscreen ? (
        <Icon name="close" size="md" />
      ) : (
        <ButtonIcon size="14px" lineHeight="15px" className="fas fa-expand image-gallery-svg" />
      )}
    </FullScreenButtonWrap>
  )
}

export const ListingGallery: FC<ListingGalleryProps> = (props: ListingGalleryProps) => {
  const { canFullScreen } = props
  const track = useTracker()
  const photos = useSelector(selectPublicListingPhotos)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const listingId = useContext(ListingIdContext)

  const onScreenChange = () => {
    setIsFullscreen(!isFullscreen)
  }

  const onImageClick = (event, imageGallery: RefObject<ReactImageGallery>) => {
    if (canFullScreen) {
      if (!isFullscreen) {
        track(LISTING_PUBLIC_PAGE.VIEW_IMAGE_IN_LIGHTBOX, { listing_id: listingId })
      }
      imageGallery.current?.fullScreen()
    }
  }

  const onClickWithEventTracking = (event, onClick: MouseEventHandler<HTMLElement>) => {
    if (!isFullscreen) {
      track(LISTING_PUBLIC_PAGE.VIEW_IMAGE_IN_LIGHTBOX, { listing_id: listingId })
    }
    onClick(event)
  }

  return (
    <ImageGallery
      id="imageGallery"
      items={photos}
      onImageClick={onImageClick}
      onScreenChange={onScreenChange}
      isFullscreen={isFullscreen}
      showFullscreenButton={canFullScreen}
      renderFullscreenButton={(onClick, isFullScreen) => (
        <FullScreenButton
          onClick={(e) => {
            onClickWithEventTracking(e, onClick)
          }}
          isFullscreen={isFullScreen}
        />
      )}
    />
  )
}

ListingGallery.defaultProps = {
  canFullScreen: true,
}
